import { Check, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { JobApi } from '@/modules/jobs/types';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/modules/ui/components/tooltip/Tooltip';
import { formatDateLabel } from '@/utils/dateUtils';

interface SourcingStatusTagProps {
  status: JobApi['sourcingState'];
  lastSourcing: JobApi['lastSourcing'];
}

export const SourcingStatusTag = ({
  status,
  lastSourcing,
}: SourcingStatusTagProps) => {
  const getTooltipContent = () => {
    if (!lastSourcing) return null;

    const { startedAt, finishedAt, ranByUser } = lastSourcing;
    const userName = ranByUser
      ? `${ranByUser.firstName} ${ranByUser.lastName}`
      : 'Unknown user';

    if (status === 'InProgress' && startedAt) {
      return (
        <div className="flex max-w-xs flex-col gap-1.5">
          <Paragraph variant="tooltip">
            Candidates appear here in real time as we source them.
          </Paragraph>
          <Paragraph variant="tooltip" className="font-medium">
            {formatDateLabel(startedAt, 'Started')}
          </Paragraph>
          <Paragraph variant="tooltip">
            by{' '}
            {ranByUser ? (
              <Link
                to={`/users/${ranByUser.id}/edit`}
                className="underline underline-offset-2 transition-colors hover:text-white/80"
              >
                {userName}
              </Link>
            ) : (
              userName
            )}
          </Paragraph>
        </div>
      );
    }

    if ((status === 'Finished' || status === 'Failed') && startedAt) {
      return (
        <div className="flex flex-col gap-1.5">
          <Paragraph variant="tooltip">
            Ran by{' '}
            {ranByUser ? (
              <Link
                to={`/users/${ranByUser.id}/edit`}
                className="font-medium underline underline-offset-2 transition-colors hover:text-white/80"
              >
                {userName}
              </Link>
            ) : (
              userName
            )}
          </Paragraph>
          <Paragraph variant="tooltip">
            {formatDateLabel(startedAt, 'Started')}
          </Paragraph>
          {finishedAt && (
            <Paragraph variant="tooltip">
              {formatDateLabel(finishedAt, 'Finished')}
            </Paragraph>
          )}
        </div>
      );
    }

    return null;
  };

  const tooltipContent = getTooltipContent();

  const renderBadge = () => {
    switch (status) {
      case 'New':
        return (
          <Badge variant="outline" className="text-xs">
            Sourcing starting soon
          </Badge>
        );
      case 'Draft':
        return (
          <Badge variant="outline" className="text-xs">
            Draft
          </Badge>
        );
      case 'InProgress':
        return (
          <Badge
            variant="outline"
            className="border-semantic-540 bg-semantic-540/10 text-semantic-540 text-xs"
          >
            <Loader2 size={14} className="animate-spin" />
            Sourcing in progress
          </Badge>
        );
      case 'Finished':
        return (
          <Badge
            variant="outline"
            className="border-semantic-560 bg-semantic-560/10 text-semantic-560 text-xs"
          >
            <Check size={14} />
            Sourcing finished
          </Badge>
        );
      case 'Failed':
        return (
          <Badge
            variant="outline"
            className="border-semantic-550 bg-semantic-550/10 text-semantic-550 text-xs"
          >
            <X size={14} />
            Sourcing failed
          </Badge>
        );
    }
  };

  const badge = renderBadge();

  if (tooltipContent) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div>{badge}</div>
        </TooltipTrigger>
        <TooltipContent>{tooltipContent}</TooltipContent>
      </Tooltip>
    );
  }

  return badge;
};
